import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DollarSign, ArrowLeft, RefreshCw, Loader2, Receipt, Landmark, TrendingUp,
  ChevronRight, AlertTriangle, CheckCircle2, Clock, BarChart3, Target, Gauge, FileText,
  Boxes, Box
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from 'recharts';

function fmt(val) {
  if (val === undefined || val === null || val === 0) return '0';
  if (Math.abs(val) >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (Math.abs(val) >= 100000) return `${(val / 100000).toFixed(1)} L`;
  return val.toLocaleString('en-IN', { maximumFractionDigits: 0 });
}

const STATUS_COLORS = {
  DRAFT: '#94A3B8', SUBMITTED: '#3B82F6', UNDER_REVIEW: '#143A6B', MEASURED: '#6366F1',
  CERTIFIED: '#0EA5E9', APPROVED: '#22C55E', PAYMENT_RECOMMENDED: '#14B8A6',
  PAYMENT_PROCESSED: '#06B6D4', PAID: '#10B981', REJECTED: '#EF4444'
};

function MetricTile({ label, value, color, subtext }) {
  return (
    <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
      <div className="text-[10px] font-medium mb-1" style={{ color: 'var(--text-muted)' }}>{label}</div>
      <div className="text-xl font-bold" style={{ color: color || 'var(--text-primary)' }}>{value}</div>
      {subtext && <div className="text-[10px] mt-1" style={{ color: 'var(--text-disabled)' }}>{subtext}</div>}
    </div>
  );
}

function IndexBadge({ label, value }) {
  const color = value >= 1 ? '#22C55E' : value >= 0.9 ? '#F59E0B' : '#EF4444';
  const bg = value >= 1 ? '#22C55E12' : value >= 0.9 ? '#F59E0B12' : '#EF444412';
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ backgroundColor: bg, border: `1px solid ${color}20` }}>
      <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>{label}</span>
      <span className="text-lg font-bold" style={{ color }}>{value?.toFixed(2) || '—'}</span>
    </div>
  );
}

function QuickNavCard({ icon: Icon, title, subtitle, color, onClick }) {
  return (
    <button onClick={onClick} className="w-full text-left rounded-xl p-4 group transition-all hover:-translate-y-0.5"
      style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }}
      data-testid={`nav-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg" style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
          <Icon className="w-4 h-4" style={{ color }} />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</div>
          <div className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{subtitle}</div>
        </div>
        <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--text-muted)' }} />
      </div>
    </button>
  );
}

export default function CostManagementSummary() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(true);
  const [costSummary, setCostSummary] = useState(null);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [wcMetrics, setWcMetrics] = useState(null);
  const [evmTrend, setEvmTrend] = useState([]);

  useEffect(() => {
    api.get('/v1/cc/projects-with-data').then(r => {
      setProjects(r.data.projects || []);
      if (r.data.projects?.length > 0) setSelectedProject(r.data.projects[0].project_id);
    }).catch(() => toast.error('Failed to load projects'));
  }, []);

  const loadData = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const [csRes, isRes, wcRes, trRes] = await Promise.all([
        api.get(`/v1/projects/${selectedProject}/cc/summary`),
        api.get(`/v1/projects/${selectedProject}/cc/invoices/summary`),
        api.get(`/v1/projects/${selectedProject}/cc/cash-flow/working-capital`).catch(() => ({ data: {} })),
        api.get(`/v1/projects/${selectedProject}/cc/evm/trend`).catch(() => ({ data: { trend: [] } })),
      ]);
      setCostSummary(csRes.data);
      setInvoiceSummary(isRes.data);
      setWcMetrics(wcRes.data);
      setEvmTrend(trRes.data?.trend || []);
    } catch {
      toast.error('Failed to load summary data');
    } finally {
      setLoading(false);
    }
  }, [selectedProject]);

  useEffect(() => { loadData(); }, [loadData]);

  const evm = costSummary?.evm || {};

  const budgetData = useMemo(() => {
    if (!costSummary) return [];
    return [
      { name: 'Original Budget', value: costSummary.originalBudget, color: '#3B82F6' },
      { name: 'Approved Changes', value: Math.abs(costSummary.approvedChanges), color: '#143A6B' },
      { name: 'Current Budget', value: costSummary.currentBudget, color: '#0EA5E9' },
      { name: 'Committed', value: costSummary.committed, color: '#F59E0B' },
      { name: 'Invoiced', value: costSummary.invoiced, color: '#F97316' },
      { name: 'Paid', value: costSummary.paid, color: '#22C55E' },
    ];
  }, [costSummary]);

  const pipelineData = useMemo(() => {
    if (!invoiceSummary?.byStatus) return [];
    const statusOrder = ['DRAFT', 'SUBMITTED', 'UNDER_REVIEW', 'MEASURED', 'CERTIFIED', 'APPROVED', 'PAYMENT_RECOMMENDED', 'PAYMENT_PROCESSED', 'PAID'];
    return statusOrder.filter(s => invoiceSummary.byStatus[s]).map(s => ({
      name: s.replace(/_/g, ' '), value: invoiceSummary.byStatus[s].count, amount: invoiceSummary.byStatus[s].totalGross, color: STATUS_COLORS[s] || '#94A3B8'
    }));
  }, [invoiceSummary]);

  const categoryData = useMemo(() => {
    if (!costSummary?.categoryBreakdown) return [];
    const colors = ['#3B82F6', '#143A6B', '#0EA5E9', '#F59E0B', '#EF4444', '#22C55E', '#F97316'];
    return Object.entries(costSummary.categoryBreakdown).map(([cat, data], i) => ({
      name: cat.replace(/_/g, ' '), budget: data.budget, committed: data.committed, incurred: data.incurred, paid: data.paid, color: colors[i % colors.length]
    }));
  }, [costSummary]);

  const trendData = useMemo(() => evmTrend.map(t => ({ period: t.periodId, SPI: t.spi, CPI: t.cpi })), [evmTrend]);

  const projectName = projects.find(p => p.project_id === selectedProject)?.name || selectedProject;

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="cost-management-summary-page">
        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => navigate('/project-controls')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="p-2.5 rounded-xl" style={{ background: '#0EA5E912', border: '1px solid #0EA5E920' }}>
            <DollarSign className="w-5 h-5" style={{ color: '#0EA5E9' }} />
          </div>
          <div className="flex-1">
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Cost Management Summary</h1>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Executive overview — {projectName}</p>
          </div>
          <select value={selectedProject} onChange={e => setSelectedProject(e.target.value)}
            className="rounded-lg px-3 py-2 text-sm" data-testid="project-selector"
            style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', color: 'var(--text-primary)' }}>
            {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
          </select>
          <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--accent-text)' }} /></div>
        ) : (
          <>
            {/* Budget Health Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="budget-metrics">
              <MetricTile label="Original Budget" value={fmt(costSummary?.originalBudget)} color="#3B82F6" />
              <MetricTile label="Current Budget" value={fmt(costSummary?.currentBudget)} color="#0EA5E9" subtext={`Changes: ${fmt(costSummary?.approvedChanges)}`} />
              <MetricTile label="Committed" value={fmt(costSummary?.committed)} color="#F59E0B" subtext={`${costSummary?.contractCount || 0} contracts`} />
              <MetricTile label="Invoiced" value={fmt(costSummary?.invoiced)} color="#F97316" subtext={`${costSummary?.invoiceCount || 0} invoices`} />
              <MetricTile label="Paid" value={fmt(costSummary?.paid)} color="#22C55E" />
              <MetricTile label="Outstanding" value={fmt(costSummary?.outstanding)} color="#EF4444" />
            </div>

            {/* EVM Performance + Quick Nav */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {/* EVM Card */}
              <div className="lg:col-span-2 rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="evm-summary-card">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>EVM Performance</h3>
                  <div className="flex items-center gap-2">
                    <IndexBadge label="SPI" value={evm.spi} />
                    <IndexBadge label="CPI" value={evm.cpi} />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-3 mb-4">
                  <MetricTile label="BAC" value={fmt(evm.bac)} />
                  <MetricTile label="EAC" value={fmt(evm.eacSelected || evm.eacCPI)} color="#F97316" />
                  <MetricTile label="VAC" value={fmt(evm.vac)} color={evm.vac >= 0 ? '#22C55E' : '#EF4444'} />
                  <MetricTile label="Physical %" value={`${evm.physicalProgressPct || 0}%`} color="#0EA5E9" subtext={`Planned: ${evm.plannedProgressPct || 0}%`} />
                </div>
                {trendData.length > 0 && (
                  <ResponsiveContainer width="100%" height={160}>
                    <LineChart data={trendData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis dataKey="period" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                      <YAxis domain={[0.5, 1.5]} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                      <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                      <Legend wrapperStyle={{ fontSize: 10 }} />
                      <Line type="monotone" dataKey="SPI" stroke="#3B82F6" strokeWidth={2} dot={{ r: 2 }} />
                      <Line type="monotone" dataKey="CPI" stroke="#22C55E" strokeWidth={2} dot={{ r: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                )}
              </div>

              {/* Quick Nav */}
              <div className="space-y-3" data-testid="quick-nav">
                <h3 className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>Quick Access</h3>
                <QuickNavCard icon={Box} title="5D Cost Modelling" subtitle="Link 3D objects to CBS cost codes | Budget vs Actual visualization" color="#143A6B" onClick={() => navigate('/project-controls/cost/5d-model')} data-testid="5d-nav-card" />
                <QuickNavCard icon={Receipt} title="Invoice Management" subtitle={`${invoiceSummary?.totalInvoices || 0} invoices | ${fmt(invoiceSummary?.totalOutstanding)} outstanding`} color="#0EA5E9" onClick={() => navigate('/project-controls/cost/invoices')} />
                <QuickNavCard icon={Landmark} title="Working Capital" subtitle={`Peak funding: ${fmt(wcMetrics?.peakFunding)} | Avg outflow: ${fmt(wcMetrics?.avgMonthlyOutflow)}/mo`} color="#F97316" onClick={() => navigate('/project-controls/cost/working-capital')} />
                <QuickNavCard icon={TrendingUp} title="Earned Value Management" subtitle={`SPI: ${evm.spi?.toFixed(2) || '—'} | CPI: ${evm.cpi?.toFixed(2) || '—'} | BAC: ${fmt(evm.bac)}`} color="#22C55E" onClick={() => navigate('/project-controls/cost/evm')} />
                <QuickNavCard icon={FileText} title="Budget Revision Tracker" subtitle="Compare budget snapshots and track evolution" color="#3B82F6" onClick={() => navigate('/project-controls/cost/budget-revisions')} />
                <QuickNavCard icon={BarChart3} title="Commitment Tracking" subtitle={`${costSummary?.contractCount || 0} contracts | Spend analysis & variance alerts`} color="#F59E0B" onClick={() => navigate('/project-controls/cost/commitments')} />
              </div>
            </div>

            {/* Budget vs Commitment Chart + Payment Pipeline */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* Budget Waterfall */}
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="budget-chart">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Budget to Payment Flow</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={budgetData} margin={{ top: 5, right: 15, bottom: 5, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis dataKey="name" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 10 }} tickFormatter={v => fmt(v)} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} formatter={(v) => fmt(v)} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {budgetData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Payment Pipeline */}
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="pipeline-chart">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Invoice Payment Pipeline</h3>
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={pipelineData} layout="vertical" margin={{ top: 5, right: 15, bottom: 5, left: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                    <XAxis type="number" tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <YAxis dataKey="name" type="category" width={110} tick={{ fill: 'var(--text-muted)', fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 8, fontSize: 12, color: 'var(--text-primary)' }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {pipelineData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Category Breakdown */}
            {categoryData.length > 0 && (
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--card-bg)', border: '1px solid var(--border-color)' }} data-testid="category-breakdown">
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>Cost Category Breakdown</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr style={{ backgroundColor: 'var(--table-header-bg)' }}>
                        {['Category', 'Budget', 'Committed', 'Incurred', 'Paid', 'Utilization'].map(h => (
                          <th key={h} className={`p-3 font-medium ${h === 'Category' ? 'text-left' : 'text-right'}`} style={{ color: 'var(--text-muted)' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {categoryData.map((cat, i) => {
                        const util = cat.budget > 0 ? Math.round(cat.committed / cat.budget * 100) : 0;
                        return (
                          <tr key={cat.name} style={{ borderBottom: '1px solid var(--border-color)', backgroundColor: i % 2 === 0 ? 'transparent' : 'var(--table-row-alt)' }}>
                            <td className="p-3 font-medium" style={{ color: 'var(--text-primary)' }}>
                              <div className="flex items-center gap-2">
                                <div className="w-2 h-2 rounded-full" style={{ backgroundColor: cat.color }} />
                                {cat.name}
                              </div>
                            </td>
                            <td className="p-3 text-right" style={{ color: 'var(--text-primary)' }}>{fmt(cat.budget)}</td>
                            <td className="p-3 text-right" style={{ color: '#F59E0B' }}>{fmt(cat.committed)}</td>
                            <td className="p-3 text-right" style={{ color: '#F97316' }}>{fmt(cat.incurred)}</td>
                            <td className="p-3 text-right" style={{ color: '#22C55E' }}>{fmt(cat.paid)}</td>
                            <td className="p-3 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--border-color)' }}>
                                  <div className="h-full rounded-full" style={{ width: `${Math.min(util, 100)}%`, backgroundColor: util > 90 ? '#EF4444' : util > 70 ? '#F59E0B' : '#22C55E' }} />
                                </div>
                                <span className="font-medium" style={{ color: util > 90 ? '#EF4444' : 'var(--text-primary)' }}>{util}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
